import { TEXT_STYLES, TextStyleKey } from "$/data/textStyles";

export type RLEMask = Array<[number, number]>;

export interface MaskSize {
  width: number;
  height: number;
}

export const CANVAS_SIZE = 400; // 600
export const FULL_CANVAS_SIZE = 512; // 768
/**
 * Checks if a mask canvas is empty (all pixels are transparent/white)
 */
export function isMaskEmpty(ctx: CanvasRenderingContext2D): boolean {
  const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
  return imageData.data.every((val) => val === 0);
}

/**
 * Gets coordinates relative to mask canvas, handling both mouse and touch events
 */
export function getMaskCoordinates(
  event:
    | React.MouseEvent<HTMLCanvasElement>
    | React.TouchEvent<HTMLCanvasElement>
    | MouseEvent
    | TouchEvent,
  canvas: HTMLCanvasElement,
  canvasSize: number,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const scale = canvasSize / canvas.clientWidth;

  if ("touches" in event) {
    return {
      x: (event.touches[0].clientX - rect.left) * scale,
      y: (event.touches[0].clientY - rect.top) * scale,
    };
  } else {
    return {
      x: (event.clientX - rect.left) * scale,
      y: (event.clientY - rect.top) * scale,
    };
  }
}

/**
 * Run-length encodes a binary mask.
 * @param mask - Binary mask as Uint8ClampedArray where each element is 0 or 1
 * @returns Array of [value, length] tuples representing runs
 */
export function rleEncode(mask: Uint8ClampedArray): RLEMask {
  const runs: RLEMask = [];
  let lastVal = mask[0];
  let runLength = 1;

  for (let i = 1; i < mask.length; i++) {
    if (mask[i] === lastVal) {
      runLength++;
    } else {
      runs.push([lastVal, runLength]);
      lastVal = mask[i];
      runLength = 1;
    }
  }
  runs.push([lastVal, runLength]); // Final run
  return runs;
}

/**
 * Creates a binary mask from canvas image data
 * White = 1, Black = 0
 * @param ctx - Canvas rendering context
 * @param invert - Whether to invert the mask
 * @returns Binary mask as Uint8ClampedArray where each element is 0 or 1
 */
export function createBinaryMask(
  ctx: CanvasRenderingContext2D,
  invert: boolean = false,
): Uint8ClampedArray {
  const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
  const binaryMask = new Uint8ClampedArray(imageData.data.length / 4);

  // Convert RGBA to binary values (0 or 1) based on alpha channel
  // Alpha < 128 becomes 1 (background), alpha >= 128 becomes 0 (foreground)
  for (let i = 0; i < imageData.data.length; i += 4) {
    const pixelValue = imageData.data[i + 3] < 128 ? 1 : 0;
    binaryMask[i / 4] = invert ? 1 - pixelValue : pixelValue;
  }

  return binaryMask;
}

/**
 * Creates a text mask canvas and returns its context and binary mask
 */
export function createTextMask(
  text: string,
  style: string,
  size: MaskSize = { width: CANVAS_SIZE, height: CANVAS_SIZE },
): {
  canvas: HTMLCanvasElement;
  binaryMask: Uint8ClampedArray;
  rleMask: RLEMask;
} {
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext("2d");

  // Configure text style
  ctx.fillStyle = "black";
  const lines = text.split("\n");
  const fontSize = Math.max(
    20,
    Math.min(250, Math.round(1000 / Math.max(...lines.map((line) => line.length)))),
  );
  ctx.font = `${
    TEXT_STYLES[style as TextStyleKey]?.fontVariant || "normal"
  } ${fontSize}px ${TEXT_STYLES[style as TextStyleKey]?.fontFamily || "Arial"}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.letterSpacing = "0.3em";

  // Draw each line of text
  const lineHeight = fontSize;
  const totalHeight = lineHeight * lines.length;
  const startY = (canvas.height - totalHeight) / 2 + lineHeight / 2;

  lines.forEach((line, index) => {
    ctx.fillText(
      line,
      canvas.width / 2,
      startY + index * lineHeight,
      canvas.width * 0.9,
    );
  });

  // Convert to binary mask
  const binaryMask = createBinaryMask(ctx);
  const rleMask = rleEncode(binaryMask);

  return { canvas, binaryMask, rleMask };
}

/**
 * Creates a binary mask and RLE encoding from a canvas
 */
export function createMaskFromCanvas(ctx: CanvasRenderingContext2D): {
  binaryMask: Uint8ClampedArray;
  rleMask: RLEMask;
  img: HTMLImageElement;
} {
  const binaryMask = createBinaryMask(ctx);

  // Create a new binary mask for FULL_CANVAS_SIZE with white (0) background
  const fullBinaryMask = new Uint8ClampedArray(FULL_CANVAS_SIZE * FULL_CANVAS_SIZE);
  fullBinaryMask.fill(1);

  // Calculate starting offset to center the canvas data
  const offsetX = (FULL_CANVAS_SIZE - ctx.canvas.width) / 2;
  const offsetY = (FULL_CANVAS_SIZE - ctx.canvas.height) / 2;

  // Copy the canvas binary mask into the centered position in the full mask
  for (let y = 0; y < ctx.canvas.height; y++) {
    for (let x = 0; x < ctx.canvas.width; x++) {
      const sourceIndex = y * ctx.canvas.width + x;
      const targetIndex =
        (offsetY + (ctx.canvas.height - 1 - y)) * FULL_CANVAS_SIZE + (x + offsetX);
      fullBinaryMask[targetIndex] = binaryMask[sourceIndex];
    }
  }
  // Save to HTMLImageElement
  const img = new Image();
  img.src = ctx.canvas.toDataURL();

  return { binaryMask: fullBinaryMask, rleMask: rleEncode(fullBinaryMask), img };
}
